// Step 16 — Contract draft detail modal.
//
// Human-only surface for review, edit, approve, and send-for-signature.
// The "AI-genererat" indicator is visible whenever source==='ai'.
// Once status leaves editable, body/variables become read-only and
// the mutation controls collapse — mirrors the server-side
// immutability guarantee so the UI can't lie.

import { useCallback, useEffect, useState } from "react";

import {
  approveContract,
  cancelContract,
  contractPdfUrl,
  getContract,
  getContractHistory,
  getContractSignatureStatus,
  rejectContract,
  sendContractForSignature,
  submitContractForReview,
  supersedeContract,
  updateContract,
} from "./api";
import { KIND_LABEL, SOURCE_LABEL, STATUS_LABEL } from "./ContractsPanel";


export default function ContractDetail({
  workspaceId, contractId, onClose, onChanged,
}) {
  const [contract, setContract] = useState(null);
  const [history, setHistory] = useState([]);
  const [sigStatus, setSigStatus] = useState(null);
  const [error, setError] = useState(null);

  const [editingBody, setEditingBody] = useState(null);
  const [editingTitle, setEditingTitle] = useState(null);

  const reload = useCallback(async () => {
    if (!workspaceId || !contractId) return;
    setError(null);
    try {
      const [c, h] = await Promise.all([
        getContract(workspaceId, contractId),
        getContractHistory(workspaceId, contractId),
      ]);
      setContract(c);
      setHistory(h?.results || []);
      // Signature status is a separate call — only meaningful once
      // the contract has been sent.
      if (c && (c.status === "sent_for_signature" || c.status === "signed")) {
        try {
          setSigStatus(await getContractSignatureStatus(workspaceId, contractId));
        } catch { /* non-fatal */ }
      } else {
        setSigStatus(null);
      }
    } catch (e) {
      setError(e?.body?.detail || e.message || "Kunde inte ladda kontraktet.");
    }
  }, [workspaceId, contractId]);

  useEffect(() => { reload(); }, [reload]);

  const runVerb = useCallback(
    async (fn, args) => {
      setError(null);
      try {
        await fn(workspaceId, contractId, args || {});
        await reload();
        onChanged?.();
      } catch (e) {
        setError(e?.body?.detail || e.message || "Åtgärden misslyckades.");
      }
    },
    [workspaceId, contractId, reload, onChanged],
  );

  const saveEdits = useCallback(async () => {
    if (!contract) return;
    const patch = {};
    if (editingTitle != null && editingTitle !== contract.title) {
      patch.title = editingTitle;
    }
    if (editingBody != null && editingBody !== contract.body) {
      patch.body = editingBody;
    }
    if (Object.keys(patch).length === 0) {
      setEditingBody(null);
      setEditingTitle(null);
      return;
    }
    setError(null);
    try {
      const updated = await updateContract(workspaceId, contractId, patch);
      setContract(updated);
      setEditingBody(null);
      setEditingTitle(null);
      await reload();
      onChanged?.();
    } catch (e) {
      setError(e?.body?.detail || e.message || "Redigering misslyckades.");
    }
  }, [workspaceId, contractId, contract, editingBody, editingTitle, reload, onChanged]);

  if (!contractId) return null;

  return (
    <div className="qai-ct-detail-backdrop" onClick={onClose}>
      <div
        className="qai-ct-detail"
        role="dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="qai-ct-detail-header">
          <div className="qai-ct-detail-title">
            {contract?.title || "Laddar…"}
            {contract?.source === "ai" && (
              <span className="qai-ct-tag qai-ct-tag-ai">AI-utkast</span>
            )}
          </div>
          <button className="qai-ct-detail-close" onClick={onClose}>×</button>
        </div>

        {error && <div className="qai-ct-error">{error}</div>}

        {contract && (
          <>
            <MetaGrid c={contract} />

            <Section title="Innehåll">
              {editingBody != null ? (
                <>
                  <input
                    className="qai-ct-detail-input"
                    value={editingTitle ?? contract.title}
                    onChange={(e) => setEditingTitle(e.target.value)}
                    placeholder="Titel"
                  />
                  <textarea
                    className="qai-ct-detail-textarea qai-ct-detail-body-edit"
                    value={editingBody}
                    onChange={(e) => setEditingBody(e.target.value)}
                    rows={22}
                  />
                  <div className="qai-ct-detail-btns">
                    <Btn kind="primary" onClick={saveEdits}>Spara ändringar</Btn>
                    <Btn onClick={() => { setEditingBody(null); setEditingTitle(null); }}>
                      Avbryt
                    </Btn>
                  </div>
                </>
              ) : (
                <>
                  <pre className="qai-ct-detail-body">{contract.body}</pre>
                  {contract.is_editable && (
                    <Btn
                      onClick={() => {
                        setEditingBody(contract.body);
                        setEditingTitle(contract.title);
                      }}
                    >
                      Redigera
                    </Btn>
                  )}
                  {!contract.is_editable && (
                    <div className="qai-ct-locked-note">
                      Innehållet är låst — kontraktet är {STATUS_LABEL[contract.status]?.toLowerCase() || contract.status}.
                    </div>
                  )}
                </>
              )}
            </Section>

            <Section title="Variabler & källhänvisning">
              {contract.variables?.length ? (
                <ul className="qai-ct-detail-list">
                  {contract.variables.map((v, i) => (
                    <li key={i}>
                      <code className="qai-ct-var-tag">{`{{${v.tag_name}}}`}</code>
                      {" → "}
                      <strong>{v.value || <em>tomt</em>}</strong>
                      {v.provenance && (
                        <span className="qai-ct-provenance">
                          {" "}({v.provenance.source_type}
                          {v.provenance.source_id
                            ? ` · ${v.provenance.source_id}` : ""}
                          {v.provenance.label
                            ? ` · ${v.provenance.label}` : ""})
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="qai-ct-empty">Inga variabler registrerade.</div>
              )}
            </Section>

            {contract.clauses_included?.length > 0 && (
              <Section title="Klausuler">
                <ul className="qai-ct-detail-list">
                  {contract.clauses_included.map((cl, i) => (
                    <li key={i}>{cl}</li>
                  ))}
                </ul>
              </Section>
            )}

            {contract.uncertainties?.length > 0 && (
              <Section title="Osäkerheter (kräver manuell granskning)">
                <ul className="qai-ct-detail-list qai-ct-uncertainty-list">
                  {contract.uncertainties.map((u, i) => (
                    <li key={i}>{u}</li>
                  ))}
                </ul>
              </Section>
            )}

            {contract.approved_body_hash && (
              <Section title="Frusen godkänd version">
                <div className="qai-ct-detail-line">
                  Hash: <code>{contract.approved_body_hash}</code>
                </div>
                {contract.has_approved_pdf && (
                  <a
                    className="qai-ct-btn qai-ct-btn-primary"
                    href={contractPdfUrl(workspaceId, contract.id)}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    Visa godkänd PDF
                  </a>
                )}
              </Section>
            )}

            {sigStatus?.signing_request && (
              <Section title="Signeringsstatus">
                <div className="qai-ct-detail-line">
                  Submission: <code>{sigStatus.signing_request.submission_id}</code>
                </div>
                <div className="qai-ct-detail-line">
                  Status: {sigStatus.signing_request.status}
                </div>
                {sigStatus.signing_request.signing_url && (
                  <a
                    className="qai-ct-btn qai-ct-btn-ghost"
                    href={sigStatus.signing_request.signing_url}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    Öppna signeringssida
                  </a>
                )}
              </Section>
            )}

            <div className="qai-ct-detail-controls">
              <Controls c={contract} runVerb={runVerb} />
            </div>

            <Section title="Historik">
              <ol className="qai-ct-detail-history">
                {history.map((e) => (
                  <li key={e.id}>
                    <span className="qai-ct-history-kind">{e.event_type}</span>
                    {e.from_status && e.to_status && (
                      <span className="qai-ct-history-transition">
                        {" "}({e.from_status} → {e.to_status})
                      </span>
                    )}
                    {e.reason && (
                      <span className="qai-ct-history-reason">
                        {" "} — "{e.reason}"
                      </span>
                    )}
                    <span className="qai-ct-history-at">
                      {" "} · {new Date(e.created_at).toLocaleString("sv-SE")}
                    </span>
                  </li>
                ))}
              </ol>
            </Section>
          </>
        )}

        <div className="qai-ct-detail-footer-note">
          Sigge har inga verktyg för att godkänna eller signera. Alla
          externa åtgärder utförs manuellt via knapparna här ovan.
        </div>
      </div>
    </div>
  );
}


function Section({ title, children }) {
  return (
    <section className="qai-ct-detail-section">
      <h4>{title}</h4>
      {children}
    </section>
  );
}


function MetaGrid({ c }) {
  return (
    <div className="qai-ct-detail-meta">
      <MetaRow label="Typ" value={KIND_LABEL[c.kind] || c.kind} />
      <MetaRow label="Status" value={STATUS_LABEL[c.status] || c.status} />
      <MetaRow label="Källa" value={SOURCE_LABEL[c.source] || c.source} />
      <MetaRow label="Köpare" value={c.acquirer_name || "—"} />
      <MetaRow label="Signer" value={c.signer_email || c.signer_name || "—"} />
      <MetaRow label="Skapad" value={c.created_at ? new Date(c.created_at).toLocaleString("sv-SE") : "—"} />
      {c.approved_at && (
        <MetaRow label="Godkänd" value={new Date(c.approved_at).toLocaleString("sv-SE")} />
      )}
      {c.signed_at && (
        <MetaRow label="Signerad" value={new Date(c.signed_at).toLocaleString("sv-SE")} />
      )}
    </div>
  );
}


function MetaRow({ label, value }) {
  return (
    <div className="qai-ct-detail-meta-row">
      <span className="qai-ct-detail-meta-label">{label}</span>
      <span className="qai-ct-detail-meta-value">{value}</span>
    </div>
  );
}


function Controls({ c, runVerb }) {
  const btns = [];
  const s = c.status;
  if (s === "drafting") {
    btns.push(<Btn key="review" onClick={() => runVerb(submitContractForReview)}>Skicka för granskning</Btn>);
    btns.push(<Btn key="approve" kind="primary" onClick={() => runVerb(approveContract)}>Godkänn & frys</Btn>);
    btns.push(<Btn key="reject" onClick={() => {
      const r = window.prompt("Anledning (valfritt)") || "";
      runVerb(rejectContract, { reason: r });
    }}>Avvisa</Btn>);
  }
  if (s === "pending_review") {
    btns.push(<Btn key="approve" kind="primary" onClick={() => runVerb(approveContract)}>Godkänn & frys</Btn>);
    btns.push(<Btn key="reject" onClick={() => {
      const r = window.prompt("Anledning (valfritt)") || "";
      runVerb(rejectContract, { reason: r });
    }}>Avvisa</Btn>);
  }
  if (s === "approved") {
    btns.push(
      <Btn key="send" kind="primary" onClick={() => {
        const email = window.prompt(
          "Signer-epost (måste matcha kopplad kontakt om sådan finns):",
          c.signer_email || "",
        );
        if (!email) return;
        const name = window.prompt("Signer-namn:", c.signer_name || "") || "";
        runVerb(sendContractForSignature, { signer_email: email, signer_name: name });
      }}>Skicka för signering</Btn>,
    );
  }
  const isOpen = ["drafting", "pending_review", "approved", "sent_for_signature"].includes(s);
  if (isOpen) {
    btns.push(
      <Btn key="cancel" onClick={() => {
        const r = window.prompt(
          s === "sent_for_signature"
            ? "Signering har startat. Ange orsak för avbrytning:"
            : "Avbryt kontrakt — anledning (valfritt):",
        ) || "";
        if (s === "sent_for_signature" && !r.trim()) return;
        runVerb(cancelContract, { reason: r });
      }}>Avbryt</Btn>,
    );
    btns.push(<Btn key="supersede" onClick={() => runVerb(supersedeContract)}>Ersätt</Btn>);
  }
  return <div className="qai-ct-detail-btns">{btns}</div>;
}


function Btn({ kind = "ghost", onClick, children }) {
  return (
    <button
      type="button"
      className={`qai-ct-btn qai-ct-btn-${kind}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
